import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { QrCode, Camera, CheckCircle2, XCircle, Calendar, Clock, User, MapPin, Package } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function MemberQRCheckin() {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages", "qr"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [todayReservations, setTodayReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrInput, setQrInput] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchTodayReservations();
    return () => {
      stopCamera();
    };
  }, []);

  const fetchTodayReservations = async () => {
    try {
      const response = await fetch(`${API}/members/me/reservations?date=${format(new Date(), "yyyy-MM-dd")}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setTodayReservations(data.filter(r => r.status === "confirmed" || r.status === "completed"));
      }
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setScanning(true);
    } catch (error) {
      console.error("Camera error:", error);
      toast.error(t("common:toast_kameraAcilamadiLutfenKameraIzni"));
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  // Handle QR code scan - this is called when member scans the QR displayed by trainer
  const handleQRScan = async (qrCode) => {
    if (!qrCode || qrCode.length < 10) return;
    
    try {
      const response = await fetch(`${API}/qr/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ qr_code: qrCode }),
      });

      const data = await response.json();

      if (response.ok) {
        setResult({ 
          success: true, 
          message: data.message || t("common:lessonCompleted"),
          details: data
        });
        toast.success(t("common:toast_dersTamamlandi"));
        stopCamera();
        setQrInput("");
        fetchTodayReservations();
      } else {
        setResult({ 
          success: false, 
          message: data.detail || t("admin:invalidQR") 
        });
        toast.error(data.detail || t("admin:invalidQR"));
      }
    } catch (error) {
      setResult({ success: false, message: t('common:errorOccurred') });
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQrInput(value);
    
    // Auto-submit when QR code is detected (starts with SPOR-)
    if (value.startsWith("SPOR-") && value.length >= 16) {
      handleQRScan(value);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && qrInput) {
      handleQRScan(qrInput);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading font-bold text-xl text-navy">{t("admin:qrIleDersOnayi")}</h2>
        <p className="text-grey text-sm">{t("admin:antrenorunuzunGosterdigiQrKoduTarayarak")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QR Scanner */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              QR Kod Tara
            </CardTitle>
          </CardHeader>
          <CardContent>
            {scanning ? (
              <div className="space-y-4">
                <div className="relative aspect-square bg-black rounded-xl overflow-hidden">
                  <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    playsInline
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 border-4 border-white/50 rounded-2xl animate-pulse"></div>
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 text-center text-white text-sm">
                    QR kodu çerçeve içine hizalayın
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  className="w-full rounded-xl"
                  onClick={stopCamera}
                >
                  Kamerayı Kapat
                </Button>
                
                {/* Manual QR input */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-grey mb-2">QR kodu elle girin:</p>
                  <Input
                    placeholder={t("admin:ph_qrKoduYapistirinVeyaYazin")}
                    value={qrInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    className="rounded-xl"
                    data-testid="qr-manual-input"
                  />
                  <Button
                    className="w-full mt-2 bg-navy hover:bg-navy/90 rounded-xl"
                    onClick={() => handleQRScan(qrInput)}
                    disabled={!qrInput}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Dersi Tamamla
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-24 h-24 bg-navy/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Camera className="w-12 h-12 text-navy" />
                </div>
                <p className="text-grey mb-4">
                  Antrenörünüzün gösterdiği QR kodu taratmak için kamerayı açın
                </p>
                <Button
                  className="bg-navy hover:bg-navy/90 rounded-xl"
                  onClick={startCamera}
                  data-testid="start-camera-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Kamerayı Aç
                </Button>
                
                {/* Alternative: Manual input without camera */}
                <div className="mt-6 p-4 bg-slate-50 rounded-xl text-left">
                  <p className="text-sm font-medium text-navy mb-2">Kamera kullanmadan:</p>
                  <Input
                    placeholder={t("admin:ph_qrKoduBurayaYapistirin")}
                    value={qrInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    className="rounded-xl"
                  />
                  <Button
                    className="w-full mt-2 bg-green-600 hover:bg-green-700 rounded-xl"
                    onClick={() => handleQRScan(qrInput)}
                    disabled={!qrInput}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Dersi Tamamla
                  </Button>
                </div>
              </div>
            )}

            {/* Result */}
            {result && (
              <div className={`mt-4 p-4 rounded-xl ${result.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {result.success ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                  <span className={`font-medium ${result.success ? "text-green-800" : "text-red-800"}`}>
                    {result.message}
                  </span>
                </div>
                {result.success && result.details && (
                  <div className="text-sm text-green-700 space-y-1 mt-2 pl-8">
                    {result.details.member_name && (
                      <p>Üye: {result.details.member_name}</p>
                    )}
                    {result.details.branch && (
                      <p>Branş: {getLocalizedBranch(result.details.branch)}</p>
                    )}
                    {result.details.remaining_sessions !== null && result.details.remaining_sessions !== undefined && (
                      <p className="flex items-center gap-1">
                        <Package className="w-4 h-4" />
                        Kalan ders hakkı: <strong>{result.details.remaining_sessions}</strong>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's Reservations */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Bugünkü Derslerim
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayReservations.length > 0 ? (
              <div className="space-y-3">
                {todayReservations.map((res) => (
                  <div
                    key={res.reservation_id}
                    className={`p-4 rounded-xl ${
                      res.status === "completed" 
                        ? "bg-green-50 border border-green-200" 
                        : "bg-slate-50"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="w-4 h-4 text-grey" />
                          <span className="font-semibold">{res.start_time} - {res.end_time || "-"}</span>
                          <Badge 
                            variant={res.status === "completed" ? "default" : "secondary"}
                            className={res.status === "completed" ? "bg-green-600" : ""}
                          >
                            {res.status === "completed" ? t("common:completed") : "Bekliyor"}
                          </Badge>
                        </div>
                        <div className="text-sm text-grey space-y-1 mt-2">
                          {res.trainer_name && (
                            <p className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Antrenör: {res.trainer_name}
                            </p>
                          )}
                          {res.branch && (
                            <Badge variant="outline" className="text-xs">
                              {getLocalizedBranch(res.branch)}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {res.status === "completed" && (
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-grey">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Bugün için rezervasyonunuz bulunmuyor.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="border-0 shadow-sm bg-blue-50">
        <CardContent className="p-6">
          <h4 className="font-semibold text-blue-900 mb-3">Ders Nasıl Tamamlanır?</h4>
          <ol className="space-y-2 text-blue-800 text-sm">
            <li className="flex items-start gap-2">
              <span className="font-bold">1.</span>
              <span>{t("admin:antrenorunuzDersinizIcinBirQr")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">2.</span>
              <span>{t("admin:telefonunuzlaBuQrKoduTarayin")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">3.</span>
              <span>{t("packages:dersinizOtomatikOlarakTamamlanirVe")}</span>
            </li>
            <li className="flex items-start gap-2 text-blue-600">
              <span className="font-bold">*</span>
              <span>{t("qr:qrOkutmayiUnutursanizAntrenorunuzDersi")}</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
